'use client';

import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronRight } from 'lucide-react';

const navLinks = [
  { label: 'Work', href: '#work' },
  { label: 'Services', href: '#services' },
  { label: 'Tiers', href: '#tiers' },
  { label: 'Team', href: '#team' },
  { label: 'Contact', href: '#contact' },
];

export default function Topbar() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleNavClick = (href: string) => {
    setDrawerOpen(false);
    setActiveSection(href);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-studio-950/95 backdrop-blur-md border-b border-studio-800/80 shadow-lg shadow-black/30'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3 group focus:outline-none"
              aria-label="NASARC — Back to top"
            >
              <AppLogo size={32} />
              <span className="font-display font-800 text-xl tracking-[0.12em] text-white uppercase">
                NASARC
              </span>
            </button>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1" aria-label="Primary navigation">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className={`px-4 py-2 text-sm font-medium tracking-wide rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gold-500/40 ${
                    activeSection === link.href
                      ? 'text-gold-400 bg-gold-500/10' :'text-studio-200 hover:text-white hover:bg-studio-800/60'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* CTA + Mobile Toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleNavClick('#contact')}
                className="hidden md:flex items-center gap-2 px-5 py-2.5 gradient-gold text-studio-950 text-sm font-semibold rounded-lg transition-all duration-200 hover:opacity-90 active:scale-95 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
              >
                Start a Project
                <ChevronRight size={14} />
              </button>

              <button
                onClick={() => setDrawerOpen(!drawerOpen)}
                className="md:hidden p-2 rounded-lg text-studio-200 hover:text-white hover:bg-studio-800/60 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gold-500/40"
                aria-label={drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={drawerOpen}
              >
                {drawerOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-72 md:hidden bg-studio-900 border-l border-studio-700/60 shadow-2xl transition-transform duration-300 ease-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-between p-5 border-b border-studio-700/60">
          <div className="flex items-center gap-3">
            <AppLogo size={28} />
            <span className="font-display font-bold text-lg tracking-[0.1em] text-white uppercase">NASARC</span>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-2 rounded-lg text-studio-300 hover:text-white hover:bg-studio-700/60 transition-colors"
            aria-label="Close navigation menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="p-4 space-y-1" aria-label="Mobile navigation">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left rounded-xl text-studio-100 hover:text-white hover:bg-studio-700/50 transition-all duration-200 group"
            >
              <span className="font-medium text-base tracking-wide">{link.label}</span>
              <ChevronRight size={16} className="text-studio-400 group-hover:text-gold-400 group-hover:translate-x-0.5 transition-all duration-200" />
            </button>
          ))}
        </nav>

        <div className="absolute bottom-8 left-4 right-4">
          <button
            onClick={() => handleNavClick('#contact')}
            className="w-full py-3.5 gradient-gold text-studio-950 font-semibold rounded-xl transition-all duration-200 hover:opacity-90 active:scale-95"
          >
            Start a Project
          </button>
        </div>
      </div>
    </>
  );
}